import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import Button from '@/components/ui/Button';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { postAuth } from '@/api/ApiCalls';
import { downloadStoredDocument } from '@/helpers/downloadFile';
import { formatDateTimeLocale } from '@/helpers/dateHelper';
import { LandingSiteFormDtoIn, LandingSiteFormVersionDtoIn } from '@/models/in/LandingSiteFormDtoIn';
import PopupDialog from './PopupDialog';
import { LANDING_SITE_FORMS_URL, PUBLIC_DETAILS_PAGES, describeInFull } from './landingSiteForms';
import styles from './LandingSiteFormsPage.module.scss';

interface FormDetailsDialogProps {
  form: LandingSiteFormDtoIn;
  onClose: () => void;
  onChange: () => void;
  onRestored: () => void;
}

/**
 * What sits behind one tile. The panel at the top is what the site is serving right now - a document
 * or an address is named rather than rendered, since an admin opening this knows the document and
 * wants to act on it, while a set of particulars is laid out in full, because there the values are
 * the entry. Everything that was there before it waits behind the row below, closed until asked for.
 */
const FormDetailsDialog = ({ form, onClose, onChange, onRestored }: FormDetailsDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();

  const label = t(`landingSiteForms.items.${form.key}`);
  const isDocument = form.kind === 'File';
  const isDetails = form.kind === 'Details';
  const changeLabel = isDocument
    ? t('landingSiteForms.replace')
    : isDetails
      ? t('landingSiteForms.editDetails')
      : t('landingSiteForms.editLink');

  const historyUrl = `${LANDING_SITE_FORMS_URL}/${form.key}/history`;
  const { data: history, isLoading: isHistoryLoading } =
    useGetAuthApi<LandingSiteFormVersionDtoIn[]>(historyUrl);

  /**
   * A set of particulars is a popup inside the landing site's home page, so the API has no address
   * for it - but there is a page that shows it on its own, and that is the link the office sends a
   * donor who is only being asked for a transfer.
   */
  const publicPageUrl = isDetails ? PUBLIC_DETAILS_PAGES[form.key] : undefined;

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [hasCopiedLink, setHasCopiedLink] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The bytes come back through our API rather than off the link, which is signed for viewing and
   * cannot be fetched cross-origin. A document still served from inside the site is not ours to
   * re-serve, so that round-trip fails and opening it in a tab is the honest fallback.
   */
  const download = async () => {
    if (isDownloading || !form.url) return;

    setIsDownloading(true);
    try {
      await downloadStoredDocument(form.url, form.fileName?.trim() || t('downloadFile.fallbackFileName'));
    } catch {
      window.open(form.url, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  const copyPublicPageLink = async () => {
    if (!publicPageUrl) return;

    try {
      await navigator.clipboard.writeText(publicPageUrl);
      setHasCopiedLink(true);
      setTimeout(() => setHasCopiedLink(false), 2000);
    } catch {
      // The address is on screen and selectable, so a browser that refuses the clipboard
      // leaves the admin able to copy it by hand rather than with nothing at all.
    }
  };

  const restore = async (version: LandingSiteFormVersionDtoIn) => {
    setError(null);
    setRestoringId(version.id);
    try {
      await postAuth(
        `${LANDING_SITE_FORMS_URL}/${form.key}/restore/${version.id}`,
        { Authorization: `Bearer ${currentUser?.token}` },
        {}
      );
      onRestored();
    } catch {
      setError(t('landingSiteForms.history.restoreError'));
    } finally {
      setRestoringId(null);
    }
  };

  const previousVersions = (history ?? []).filter((version) => !version.isCurrent);

  return (
    <PopupDialog title={label} onClose={onClose}>
      <div className={styles.detailsBody}>
        <div className={styles.currentPanel}>
          <div className={styles.currentInfo}>
            <span className={`body-xs-sm-regular ${styles.currentLabel}`}>{currentLabelKey(form.kind, t)}</span>

            {isDetails ? (
              <dl className={styles.currentDetails}>
                {form.fields.map((field) => (
                  <div key={field.key} className={styles.currentDetailRow}>
                    <dt className={`body-xs-sm-regular ${styles.currentDetailLabel}`}>
                      {t(`landingSiteForms.fields.${form.key}.${field.key}`)}
                    </dt>
                    <dd className={`body-sm-semibold ${styles.currentDetailValue}`}>
                      {form.details?.[field.key]}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <span className={`body-md-semibold ${styles.currentValue}`}>{describeInFull(form)}</span>
            )}

            <span className={`body-xs-sm-regular ${styles.currentMeta}`}>{publishedLine(form, t)}</span>
          </div>

          <div className={styles.panelActions}>
            {isDocument && (
              <button
                type="button"
                className={styles.iconAction}
                title={t('downloadFile.title')}
                aria-label={t('downloadFile.title')}
                disabled={isDownloading}
                onClick={download}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </button>
            )}
            {/* Nothing to open on a set of particulars: it is read here, not somewhere else. */}
            {form.url && <OpenInNewTabAction url={form.url} label={t('landingSiteForms.openInNewTab')} />}
            <button
              type="button"
              className={styles.iconAction}
              title={changeLabel}
              aria-label={changeLabel}
              onClick={onChange}
            >
              <EditOutlinedIcon fontSize="small" />
            </button>
          </div>
        </div>

        {publicPageUrl && (
          <div className={styles.publicPagePanel}>
            <div className={styles.currentInfo}>
              <span className={`body-xs-sm-regular ${styles.currentLabel}`}>
                {t('landingSiteForms.details.publicPage')}
              </span>
              <a
                href={publicPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`body-sm-semibold ${styles.publicPageLink}`}
              >
                {publicPageUrl}
              </a>
            </div>

            <div className={styles.panelActions}>
              <button
                type="button"
                className={styles.iconAction}
                title={hasCopiedLink ? t('landingSiteForms.details.linkCopied') : t('landingSiteForms.details.copyLink')}
                aria-label={hasCopiedLink ? t('landingSiteForms.details.linkCopied') : t('landingSiteForms.details.copyLink')}
                onClick={copyPublicPageLink}
              >
                {hasCopiedLink ? <CheckIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
              </button>
              <OpenInNewTabAction url={publicPageUrl} label={t('landingSiteForms.openInNewTab')} />
            </div>
          </div>
        )}

        <div className={styles.historyPanel}>
          <button
            type="button"
            className={styles.historyToggle}
            aria-expanded={isHistoryOpen}
            onClick={() => setIsHistoryOpen((isOpen) => !isOpen)}
          >
            <span className={styles.historyIcon}>
              <HistoryIcon fontSize="small" />
            </span>
            <span className="body-md-semibold">{t('landingSiteForms.history.title')}</span>
            <span className={`body-sm-regular ${styles.historyCount}`}>
              {isHistoryLoading ? t('loading') : previousVersions.length}
            </span>
            <span className={isHistoryOpen ? styles.historyChevronOpen : styles.historyChevron}>
              <ExpandMoreIcon fontSize="small" />
            </span>
          </button>

          {isHistoryOpen && (
            previousVersions.length === 0 ? (
              <span className={`body-sm-regular ${styles.historyEmpty}`}>
                {isHistoryLoading ? t('loading') : t('landingSiteForms.history.empty')}
              </span>
            ) : (
              <ul className={styles.historyList}>
                {previousVersions.map((version) => (
                  <li key={version.id} className={styles.historyItem}>
                    <div className={styles.historyInfo}>
                      <span className={`body-sm-semibold ${styles.historyValue}`}>{describeInFull(version)}</span>
                      <span className={`body-xs-sm-regular ${styles.historyMeta}`}>{publishedLine(version, t)}</span>
                    </div>

                    <div className={styles.historyActions}>
                      {version.url && (
                        <OpenInNewTabAction url={version.url} label={t('landingSiteForms.openInNewTab')} />
                      )}
                      {/* A details entry's oldest versions are from when it was still a PDF, and
                          putting one of those back would leave the popup with nothing to say. */}
                      {(!isDetails || version.details) && (
                        <button
                          type="button"
                          className={styles.iconAction}
                          title={t('landingSiteForms.history.restore')}
                          aria-label={t('landingSiteForms.history.restore')}
                          disabled={restoringId !== null}
                          onClick={() => restore(version)}
                        >
                          <SettingsBackupRestoreIcon fontSize="small" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>

        {error && <p className={`body-sm-regular ${styles.error}`}>{error}</p>}

        <div className={styles.detailsActions}>
          <Button variant="white" onClick={onClose}>{t('close')}</Button>
          <Button variant="primary" onClick={onChange}>{changeLabel}</Button>
        </div>
      </div>
    </PopupDialog>
  );
};

/**
 * Opens what an entry or one of its versions points at. Taking the address as a prop is what keeps
 * it a string: narrowing `form.url` at the call site does not survive into a click handler, since
 * nothing stops a property from changing between the render and the click.
 */
const OpenInNewTabAction = ({ url, label }: { url: string; label: string }) => (
  <button
    type="button"
    className={styles.iconAction}
    title={label}
    aria-label={label}
    onClick={() => window.open(url, '_blank', 'noopener')}
  >
    <OpenInNewIcon fontSize="small" />
  </button>
);

/** What the panel is showing, in the entry's own terms. */
const currentLabelKey = (
  kind: LandingSiteFormDtoIn['kind'],
  t: (key: string) => string
): string => {
  if (kind === 'File') return t('landingSiteForms.details.currentFile');
  if (kind === 'Details') return t('landingSiteForms.details.currentDetails');
  return t('landingSiteForms.details.currentAddress');
};

/**
 * When this version went up and who put it there. A version with no publisher is the one the site
 * was built with, which is worth saying in those words rather than leaving the line blank.
 */
const publishedLine = (
  version: { publishedAt: string | null; publishedByEmail: string | null },
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  if (!version.publishedAt) return t('landingSiteForms.details.shippedWithTheSite');

  const date = formatDateTimeLocale(version.publishedAt);

  return version.publishedByEmail
    ? t('landingSiteForms.publishedBy', { date, email: version.publishedByEmail })
    : t('landingSiteForms.details.shippedWithTheSiteSince', { date });
};

export default FormDetailsDialog;
